import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../constants/pagination.constants';
import {totalPages} from '../../../services/vte/vte-list.utils';

@Component({
    selector: 'app-vte-pagination',
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: `
        <div class="vte-pagination" *ngIf="totalRecords > 0">
            <div class="vte-pagination__summary">
                Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalRecords }}
            </div>
            <div class="vte-pagination__controls">
                <label class="vte-pagination__size">
                    <span>Rows</span>
                    <select [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
                        <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }}</option>
                    </select>
                </label>
                <button type="button" class="vte-pagination__btn" [disabled]="currentPage <= 1" (click)="changePage(-1)">
                    Previous
                </button>
                <span class="vte-pagination__page">Page {{ currentPage }} / {{ pages }}</span>
                <button type="button" class="vte-pagination__btn" [disabled]="currentPage >= pages" (click)="changePage(1)">
                    Next
                </button>
                <form
                        *ngIf="showJumpToPage && pages > 1"
                        class="vte-pagination__jump"
                        (ngSubmit)="onJumpToPage()">
                    <label>
                        <span>Jump to</span>
                        <input
                                type="number"
                                min="1"
                                [max]="pages"
                                [ngModel]="jumpToPageInput"
                                (ngModelChange)="jumpToPageInput = $event; jumpToPageError = ''"
                                name="jumpToPage"
                                inputmode="numeric"
                                aria-label="Jump to page">
                    </label>
                    <button type="submit" class="vte-pagination__btn vte-pagination__btn--jump">Go</button>
                </form>
            </div>
            <p class="vte-pagination__error" *ngIf="jumpToPageError">{{ jumpToPageError }}</p>
        </div>
    `,
    styles: [`
        .vte-pagination {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
            margin-top: 1rem;
            padding-top: 0.75rem;
            border-top: 1px solid #E5E7EB;
        }

        .vte-pagination__summary,
        .vte-pagination__page {
            font-size: 0.8125rem;
            color: #4B5563;
        }

        .vte-pagination__controls {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
        }

        .vte-pagination__jump {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
        }

        .vte-pagination__jump label {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            font-size: 0.8125rem;
            color: #4B5563;
        }

        .vte-pagination__jump input {
            width: 5rem;
            min-height: 2.25rem;
            padding: 0.35rem 0.55rem;
            border: 1px solid #D1D5DB;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            background: #fff;
        }

        .vte-pagination__size {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            font-size: 0.8125rem;
            color: #4B5563;
        }

        .vte-pagination__size select {
            min-height: 2.25rem;
            padding: 0.35rem 0.55rem;
            border: 1px solid #D1D5DB;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            background: #fff;
        }

        .vte-pagination__btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 2.25rem;
            padding: 0.55rem 1rem;
            border: 1px solid #D1D5DB;
            border-radius: 0.5rem;
            background: #fff;
            color: #374151;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
        }

        .vte-pagination__btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .vte-pagination__btn--jump {
            min-height: 2rem;
            padding: 0.45rem 0.85rem;
        }

        .vte-pagination__error {
            width: 100%;
            margin: 0;
            font-size: 0.75rem;
            color: #B91C1C;
        }
    `],
})
export class VtePaginationComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
    jumpToPageInput = '';
    jumpToPageError = '';

    @Input() currentPage = 1;
    @Input() pageSize = 10;
    @Input() totalRecords = 0;
    @Input() showJumpToPage = false;

    @Output() pageChange = new EventEmitter<number>();
    @Output() pageSizeChange = new EventEmitter<number>();

    get pages(): number {
        return totalPages(this.totalRecords, this.pageSize);
    }

    get rangeStart(): number {
        if (!this.totalRecords) {
            return 0;
        }
        return (this.currentPage - 1) * this.pageSize + 1;
    }

    get rangeEnd(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalRecords);
    }

    changePage(step: number): void {
        const next = this.currentPage + step;
        this.goToPage(next);
    }

    onPageSizeChange(size: number): void {
        this.pageSizeChange.emit(size);
    }

    onJumpToPage(): void {
        const parsed = Number(this.jumpToPageInput);
        if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
            this.jumpToPageError = 'Enter a whole page number.';
            return;
        }
        if (parsed < 1 || parsed > this.pages) {
            this.jumpToPageError = `Enter a page from 1 to ${this.pages}.`;
            return;
        }
        this.jumpToPageError = '';
        this.jumpToPageInput = '';
        this.goToPage(parsed);
    }

    private goToPage(page: number): void {
        if (page < 1 || page > this.pages || page === this.currentPage) {
            return;
        }
        this.jumpToPageError = '';
        this.pageChange.emit(page);
    }
}
